'use client';

import { Button } from '@ui/components/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ui/components/alert-dialog';
import { AppCard, PageLayout } from '@ui/components/layouts';
import { text as sharedText } from '@shared/text';
import { text } from '@modules/admin/text';

import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faCheck } from '@micks-yard/icons/solid/faCheck';
import { faDatabase } from '@micks-yard/icons/solid/faDatabase';
import { faEye } from '@micks-yard/icons/solid/faEye';
import { faSpinner } from '@micks-yard/icons/solid/faSpinner';
import { faTriangleExclamation } from '@micks-yard/icons/solid/faTriangleExclamation';
import type { AdminProps } from '@modules/admin/containers/admin_container';
import { PlantAvatarWeb } from '@modules/plants/views/plant_avatar_web';
import { PageContentLoading } from '@/src/components/PageContentLoading';

export function Admin({
  isLoading,
  isAdmin,
  allPlants,
  showClearConfirm,
  isClearSeeding,
  isSeedingGarden,
  isSeedingSummerGarden,
  isSeedingPreviewGarden,
  onOpenClearConfirm,
  onClearConfirmOpenChange,
  onClearAndSeedPlants,
  onSeedTemplateGarden,
  onSeedSummerTemplateGarden,
  onSeedPlantImagePreviewGarden,
}: AdminProps) {
  if (isLoading) {
    return <PageContentLoading />;
  }

  if (!isAdmin) {
    return (
      <PageLayout maxWidth={896}>
        <AppCard className="p-6">
          <h1 className="text-2xl font-semibold text-red-600">{text.accessDenied}</h1>
          <p className="text-muted-foreground mt-2">{text.accessDeniedDescription}</p>
        </AppCard>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={text.title} maxWidth={896}>
      <AppCard className="mb-6 p-6">
        <h2 className="mb-4 text-xl font-semibold">{text.databaseManagement}</h2>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3">
              <FaIcon icon={faTriangleExclamation} className="size-5 shrink-0 text-red-600" />
              <p className="flex-1 text-sm text-red-800">{text.clearReseedWarning}</p>
            </div>
            <Button
              type="button"
              variant="destructive"
              title={text.clearReseedTooltip}
              onClick={onOpenClearConfirm}
              disabled={isClearSeeding}>
              {isClearSeeding ? (
                <span className="flex items-center gap-2">
                  <FaIcon icon={faSpinner} className="size-4 animate-spin" />
                  {text.clearReseeding}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <FaIcon icon={faDatabase} className="size-4" />
                  {text.clearReseedButton}
                </span>
              )}
            </Button>
          </div>

          <Button
            type="button"
            title={text.seedTemplateGardenTooltip}
            onClick={onSeedTemplateGarden}
            disabled={isSeedingGarden}>
            {isSeedingGarden ? (
              <span className="flex items-center gap-2">
                <FaIcon icon={faSpinner} className="size-4 animate-spin" />
                {text.creatingGarden}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <FaIcon icon={faCheck} className="size-4" />
                {text.seedTemplateGarden}
              </span>
            )}
          </Button>

          <Button
            type="button"
            title={text.seedSummerTemplateGardenTooltip}
            onClick={onSeedSummerTemplateGarden}
            disabled={isSeedingSummerGarden}>
            {isSeedingSummerGarden ? (
              <span className="flex items-center gap-2">
                <FaIcon icon={faSpinner} className="size-4 animate-spin" />
                {text.creatingGarden}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <FaIcon icon={faCheck} className="size-4" />
                {text.seedSummerTemplateGarden}
              </span>
            )}
          </Button>

          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2 rounded-lg border border-green-300 bg-green-50 p-3">
              <FaIcon icon={faEye} className="size-5 shrink-0 text-emerald-600" />
              <p className="flex-1 text-sm text-green-800">{text.previewGardenInfo}</p>
            </div>
            <Button
              type="button"
              title={text.previewGardenTooltip}
              onClick={onSeedPlantImagePreviewGarden}
              disabled={isSeedingPreviewGarden}>
              {isSeedingPreviewGarden ? (
                <span className="flex items-center gap-2">
                  <FaIcon icon={faSpinner} className="size-4 animate-spin" />
                  {text.creatingPreviewGarden}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <FaIcon icon={faEye} className="size-4" />
                  {text.createPreviewGarden}
                </span>
              )}
            </Button>
          </div>
        </div>
      </AppCard>

      <AppCard className="p-6">
        <h2 className="mb-4 text-xl font-semibold">{text.currentPlants}</h2>

        {allPlants === undefined ? (
          <div className="flex items-center justify-center py-8">
            <FaIcon icon={faSpinner} className="size-9 animate-spin text-green-600" />
          </div>
        ) : allPlants.length === 0 ? (
          <p className="text-muted-foreground">{text.noPlants}</p>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-muted-foreground text-sm">{text.plantsTotal(allPlants.length)}</p>
            <div className="flex max-h-96 flex-wrap gap-3 overflow-auto">
              {allPlants.map((plant) => (
                <AppCard
                  key={plant._id}
                  className="hover:bg-muted/50 w-full p-3 md:w-[48%] lg:w-[31%]">
                  <div className="flex items-center gap-3">
                    <PlantAvatarWeb
                      name={plant.name}
                      emoji={plant.emoji}
                      imagePath={plant.imagePath}
                      size={32}
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-semibold">{plant.name}</span>
                      <p className="text-muted-foreground text-sm">{plant.category}</p>
                    </div>
                  </div>
                </AppCard>
              ))}
            </div>
          </div>
        )}
      </AppCard>

      <AlertDialog open={showClearConfirm} onOpenChange={onClearConfirmOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <span className="flex items-center gap-2">
                <FaIcon icon={faTriangleExclamation} className="size-5 text-red-600" />
                {text.clearConfirmTitle}
              </span>
            </AlertDialogTitle>
            <AlertDialogDescription>{text.clearConfirmDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{sharedText.cancel}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={onClearAndSeedPlants}>
              {text.clearConfirmAction}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
}
